import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Mail, Phone, MapPin, Save, ClipboardList } from "lucide-react";
import { api, apiErrorMessage } from "@/lib/api";
import LeadBidIntakeCard from "@/components/LeadBidIntakeCard";

const STATUSES = ["new", "contacted", "quoted", "converted", "closed"];

export default function LeadDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [lead, setLead] = useState(null);
    const [err, setErr] = useState("");
    const [savedMsg, setSavedMsg] = useState("");
    const [status, setStatus] = useState("");
    const [notes, setNotes] = useState("");
    const [saving, setSaving] = useState(false);

    const reloadLead = () => {
        api.get(`/admin/leads/${id}`)
            .then((r) => {
                setLead(r.data);
                setStatus(r.data.status);
                setNotes(r.data.notes || "");
            })
            .catch((e) => setErr(apiErrorMessage(e)));
    };

    useEffect(() => {
        let alive = true;
        api.get(`/admin/leads/${id}`)
            .then((r) => {
                if (!alive) return;
                setLead(r.data);
                setStatus(r.data.status);
                setNotes(r.data.notes || "");
            })
            .catch((e) => alive && setErr(apiErrorMessage(e)));
        return () => {
            alive = false;
        };
    }, [id]);

    const save = async () => {
        setSaving(true);
        setSavedMsg("");
        try {
            await api.patch(`/admin/leads/${id}`, { status, notes });
            setSavedMsg("Saved.");
            setLead((prev) => (prev ? { ...prev, status, notes } : prev));
            setTimeout(() => setSavedMsg(""), 2200);
        } catch (e) {
            setErr(apiErrorMessage(e));
        } finally {
            setSaving(false);
        }
    };

    if (err && !lead) {
        return (
            <div className="page-shell">
                <Link to="/leads" className="link-red inline-flex items-center gap-1">
                    <ArrowLeft size={14} />
                    Back to leads
                </Link>
                <div
                    className="brand-card mt-4"
                    style={{ borderColor: "var(--brand-red)", color: "var(--brand-red)" }}
                >
                    {err}
                </div>
            </div>
        );
    }
    if (!lead) {
        return (
            <div className="page-shell">
                <p className="text-muted">Loading lead…</p>
            </div>
        );
    }

    return (
        <div className="page-shell">
            <Link
                to="/leads"
                className="link-red inline-flex items-center gap-1 text-sm"
                data-testid="lead-back"
            >
                <ArrowLeft size={14} />
                Back to leads
            </Link>

            <header className="fade-up mt-4">
                <p className="eyebrow">Lead detail</p>
                <h1 className="h1 mt-3" data-testid="lead-name">
                    {lead.name}
                </h1>
                <p className="text-muted mt-2">
                    Submitted{" "}
                    {lead.created_at
                        ? new Date(lead.created_at).toLocaleString()
                        : "recently"}{" "}
                    · source: <span className="capitalize">{lead.source || "website"}</span>
                </p>
            </header>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mt-8">
                {/* Left: contact + description */}
                <div className="lg:col-span-2 flex flex-col gap-5">
                    <div className="brand-card fade-up">
                        <p className="eyebrow">Contact</p>
                        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <a
                                href={`tel:${lead.phone}`}
                                className="flex items-center gap-3 link-red"
                                data-testid="lead-phone-link"
                            >
                                <span className="metric-icon" style={{ width: 36, height: 36 }}>
                                    <Phone size={16} />
                                </span>
                                <div className="min-w-0">
                                    <p className="text-xs text-muted">Phone</p>
                                    <p className="font-semibold">{lead.phone}</p>
                                </div>
                            </a>
                            <a
                                href={`mailto:${lead.email}`}
                                className="flex items-center gap-3 link-red"
                                data-testid="lead-email-link"
                            >
                                <span className="metric-icon" style={{ width: 36, height: 36 }}>
                                    <Mail size={16} />
                                </span>
                                <div className="min-w-0">
                                    <p className="text-xs text-muted">Email</p>
                                    <p className="font-semibold truncate">{lead.email}</p>
                                </div>
                            </a>
                            {lead.property_address && (
                                <div className="flex items-center gap-3">
                                    <span
                                        className="metric-icon"
                                        style={{ width: 36, height: 36 }}
                                    >
                                        <MapPin size={16} />
                                    </span>
                                    <div className="min-w-0">
                                        <p className="text-xs text-muted">Property</p>
                                        <p className="font-semibold">
                                            {lead.property_address}
                                        </p>
                                    </div>
                                </div>
                            )}
                            <div className="flex items-center gap-3">
                                <span className="metric-icon" style={{ width: 36, height: 36 }}>
                                    <ClipboardList size={16} />
                                </span>
                                <div className="min-w-0">
                                    <p className="text-xs text-muted">Service</p>
                                    <p className="font-semibold">{lead.service_type}</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="brand-card fade-up delay-1">
                        <p className="eyebrow">Description</p>
                        <p className="mt-3 whitespace-pre-wrap" data-testid="lead-description">
                            {lead.description || "No description provided."}
                        </p>
                    </div>

                    <LeadBidIntakeCard
                        leadId={lead.id}
                        jobIds={lead.bid_intake_job_ids || []}
                        onEstimateCreated={reloadLead}
                    />

                    {lead.photo_urls?.length > 0 && (
                        <div className="brand-card fade-up delay-2">
                            <p className="eyebrow">Uploaded photos</p>
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-4">
                                {lead.photo_urls.map((u, i) => (
                                    <img
                                        key={i}
                                        src={u}
                                        alt={`Lead attachment ${i + 1}`}
                                        className="rounded-md border border-[var(--brand-surface)] w-full h-32 object-cover"
                                    />
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {/* Right: status + notes */}
                <div className="flex flex-col gap-5">
                    <div className="brand-card fade-up delay-1">
                        <p className="eyebrow">Status</p>
                        <select
                            className="field-select mt-3"
                            value={status}
                            onChange={(e) => setStatus(e.target.value)}
                            data-testid="lead-status-select"
                        >
                            {STATUSES.map((s) => (
                                <option key={s} value={s} className="capitalize">
                                    {s}
                                </option>
                            ))}
                        </select>
                        <p className="text-xs text-muted mt-2">
                            Update as the conversation progresses.
                        </p>
                    </div>

                    <div className="brand-card fade-up delay-2">
                        <p className="eyebrow">Internal notes</p>
                        <textarea
                            rows={6}
                            className="field-textarea mt-3"
                            placeholder="Track callback times, quoted price, follow-ups…"
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            data-testid="lead-notes-input"
                        />
                        <button
                            className="btn btn-primary w-full mt-4"
                            disabled={saving}
                            onClick={save}
                            data-testid="lead-save-btn"
                        >
                            <Save size={14} />
                            {saving ? "Saving…" : "Save changes"}
                        </button>
                        {savedMsg && (
                            <p
                                className="text-sm mt-3 text-center"
                                style={{ color: "var(--brand-success)" }}
                                data-testid="lead-saved-msg"
                            >
                                {savedMsg}
                            </p>
                        )}
                        {err && (
                            <p className="field-error mt-3" data-testid="lead-error">
                                {err}
                            </p>
                        )}
                    </div>

                    <div
                        className="brand-card fade-up delay-3"
                        style={{ background: "var(--brand-cream)" }}
                    >
                        <p className="eyebrow">Next step</p>
                        <p className="text-sm mt-2 text-muted">
                            Convert this lead into an estimate. The client info, address, and
                            scope description will pre-fill so you can quote in minutes.
                        </p>
                        <Link
                            to={`/estimates/new?lead=${id}`}
                            className="btn btn-primary mt-3 w-full"
                            data-testid="lead-convert-btn"
                        >
                            Create estimate from lead
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
}
